import { IconPlus, IconSparkles } from "@tabler/icons-react"
import { type FC, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"

import type { LandingPageBlogPostSummary } from "@forento/core/models/landing"

import Button from "~/components/Button"
import Layout, { PageHeader, PageTitle } from "~/components/Layout"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import AiBlogPostModal from "./AiBlogPostModal"

const header = <PageTitle>Blog</PageTitle>

const BlogPage = withAccessRequirement("landingPage", header, () => {
	const platform = usePlatform()!
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const alert = useAlert()
	const utils = query.useUtils()
	const blog = query.landing.blog.list.useQuery()

	const createPost = query.landing.blog.create.useMutation()

	const [isAiModalOpen, setAiModalOpen] = useState(searchParams.get("ai") === "true")

	const hasLandingPage = platform.landingPage?.type === "landing-page"
	const blogPosts = hasLandingPage && blog.data?.status === "ok" ? blog.data.posts : null
	const canManageBlogPosts = blogPosts !== null
	const canUseAi = canManageBlogPosts && platform.enabledFeatures.includes("ai-generated-courses")

	const handleCreatePost = async () => {
		const dialog = await alert.input(
			"New blog post",
			"Add a title for the article. This will not publish it.",
			"Title",
		)
		if (dialog.result === null) return

		try {
			const post = await createPost.mutateAsync({ title: dialog.result })
			dialog.close()
			navigate(routes.main.websiteBlogPost(post.id))
		} catch (error) {
			dialog.close()
			await alert.show("Error", error instanceof Error ? error.message : "Failed to create blog post.")
		}
	}

	return (
		<Layout>
			<AiBlogPostModal
				isOpen={isAiModalOpen && canUseAi}
				defaultLanguage={platform.language}
				onClose={() => setAiModalOpen(false)}
				onGenerated={async ({ postId, coverImageIdea, generationId }) => {
					await utils.landing.blog.list.invalidate()
					navigate(routes.main.websiteBlogPost(postId), { state: { coverImageIdea, generationId } })
				}}
			/>
			<PageHeader>
				<PageTitle>Blog</PageTitle>
				<div className="flex flex-wrap gap-3">
					{canUseAi && (
						<Button variant="secondary" onClick={() => setAiModalOpen(true)}>
							<IconSparkles className="size-4" />
							Write with Forento AI
						</Button>
					)}
					{canManageBlogPosts && (
						<Button variant="primary" onClick={handleCreatePost} isLoading={createPost.isPending}>
							<IconPlus className="size-4" />
							New post
						</Button>
					)}
				</div>
			</PageHeader>

			{blog.error ? (
				<p>Failed to load blog posts.</p>
			) : blog.data === undefined ? (
				<PartialLoadingPage />
			) : blogPosts === null ? (
				<LandingPageRequired />
			) : (
				<Table
					columns={[
						{ label: "Post", flex: { grow: 2, basis: 0 } },
						"Status",
						"Updated",
						{ label: "", flex: { grow: 0, basis: 96 } },
					]}
					data={blogPosts.map(post => [
						<PostTitle key="title" post={post} />,
						<PostStatus key="status" post={post} />,
						<span key="updated">{formatDate(post.editDate ?? post.createDate)}</span>,
						<Button key="edit" variant="secondary" onClick={routes.main.websiteBlogPost(post.id)}>
							Edit
						</Button>,
					])}
					emptyText="No blog posts yet."
				/>
			)}
		</Layout>
	)
})

const LandingPageRequired: FC = () => (
	<div className="border-component-border bg-component-background flex max-w-2xl flex-col gap-4 rounded-3xl border p-8">
		<div className="space-y-2">
			<h2 className="m-0 text-xl font-semibold">Create a landing page first</h2>
			<p className="text-component-secondary text-sm leading-relaxed">
				Blog posts are published on your landing page, so you need a landing page before creating articles.
			</p>
		</div>
		<div>
			<Button variant="primary" onClick={routes.main.websiteEditor()}>
				Open website editor
			</Button>
		</div>
	</div>
)

type PostProps = { post: LandingPageBlogPostSummary }

const PostTitle: FC<PostProps> = ({ post }) => (
	<div className="flex flex-col gap-1">
		<Link to={routes.main.websiteBlogPost(post.id)} className="hover:text-primary font-semibold text-gray-900">
			{post.title}
		</Link>
		{post.tags.length > 0 && (
			<div className="flex flex-wrap gap-1.5">
				{post.tags.map(tag => (
					<span key={tag} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
						{tag}
					</span>
				))}
			</div>
		)}
	</div>
)

const PostStatus: FC<PostProps> = ({ post }) => {
	const isPublished = post.publishDate !== null

	return (
		<span
			className={
				isPublished
					? "w-fit rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"
					: "w-fit rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600"
			}
		>
			{isPublished ? "Published" : "Draft"}
		</span>
	)
}

function formatDate(date: Date): string {
	return new Intl.DateTimeFormat("en", { dateStyle: "medium" }).format(date)
}

export default BlogPage
